import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { CircularProgress, Box } from "@mui/material";
import { Select, SelectOption } from "../../Select";
import {
  usersService,
  UnassignedPatient,
} from "../../../services/usersService";
import {
  ModalContainer,
  ModalTitle,
  ModalSubtitle,
  SelectContainer,
  ButtonsContainer,
  CancelButton,
  AssignButton,
} from "./AssignPatientModal.styled";

interface AssignPatientModalProps {
  onAssign: (patientIds: number[]) => Promise<void>;
  onCancel: () => void;
  providerId: number;
}

const AssignPatientModalContent: React.FC<AssignPatientModalProps> = ({
  onAssign,
  onCancel,
  providerId,
}) => {
  const { t } = useTranslation();
  const [patients, setPatients] = useState<UnassignedPatient[]>([]);
  const [selectedPatientIds, setSelectedPatientIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const fetchInitiatedRef = useRef(false);

  useEffect(() => {
    if (fetchInitiatedRef.current) return;
    fetchInitiatedRef.current = true;

    const fetchPatients = async () => {
      try {
        setLoading(true);
        const unassignedPatients =
          await usersService.getUnassignedPatients(providerId);
        setPatients(unassignedPatients);
      } catch (error) {
        console.error("Error fetching unassigned patients:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPatients();
  }, [providerId]);

  const formatDate = (dateString: string): string => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const patientOptions: SelectOption[] = patients.map((patient) => ({
    value: patient.id,
    label: patient.fullName,
    secondaryLabel: formatDate(patient.dateOfBirth),
  }));

  const handleSelectChange = (value: string | number | (string | number)[]) => {
    setSelectedPatientIds(value as number[]);
  };

  const handleAssign = async () => {
    if (selectedPatientIds.length === 0) return;

    try {
      setAssigning(true);
      await onAssign(selectedPatientIds);
    } catch (error) {
      console.error("Error assigning patients:", error);
    } finally {
      setAssigning(false);
    }
  };

  if (loading) {
    return (
      <ModalContainer>
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      </ModalContainer>
    );
  }

  return (
    <ModalContainer>
      <ModalTitle>{t("userProfile.assignPatientModal.title")}</ModalTitle>
      <ModalSubtitle>
        {t("userProfile.assignPatientModal.subtitle")}
      </ModalSubtitle>
      <SelectContainer>
        <Select
          name="patients"
          label={t("userProfile.assignPatientModal.selectPatients")}
          placeholder={t("userProfile.assignPatientModal.selectPatients")}
          value={selectedPatientIds}
          onChange={handleSelectChange}
          options={patientOptions}
          multiple
          showChips
        />
      </SelectContainer>
      <ButtonsContainer>
        <CancelButton
          variant="outlined"
          onClick={onCancel}
          disabled={assigning}
        >
          {t("userProfile.assignPatientModal.cancel")}
        </CancelButton>
        <AssignButton
          variant="contained"
          onClick={handleAssign}
          disabled={assigning || selectedPatientIds.length === 0}
        >
          {assigning ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            t("userProfile.assignPatientModal.assign")
          )}
        </AssignButton>
      </ButtonsContainer>
    </ModalContainer>
  );
};

export default AssignPatientModalContent;
